/**
 * useDoctorSubscription — client-side doctor subscription state hook.
 *
 * Wraps `apiClient.getSubscriptionStatus()` for the DOCTOR / TEAM_MEMBER
 * paths. Returns the derived gate state (`allowed`, `reason`, `graceEndsAt`),
 * a loading flag, and a `refresh()` for post-checkout / post-portal-return
 * refetches.
 *
 * Design notes:
 *   - Fetches on mount when `enabled` (typically `role === 'DOCTOR'`).
 *   - Refetches when the tab becomes visible again (visibilitychange) so
 *     returning from Stripe Checkout / Billing Portal in a new tab picks up
 *     the fresh state.
 *   - **Polls for ~30 seconds after returning from Stripe Checkout** when
 *     the URL contains `?subscription=success`. Stripe webhook delivery
 *     to update `subscriptionStatus` in the DB is async — the user often
 *     returns to the app BEFORE the webhook has been received/processed.
 *     Without polling, the modal would still show for that gap. Once the
 *     state transitions to allowed=true, polling stops and the URL param
 *     is cleaned up.
 *   - Errors default to allowed: true — the backend is the source of truth
 *     for enforcement (server middleware returns 402), so a frontend fetch
 *     failure shouldn't lock users out on transient network issues.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiClient, type DoctorSubscriptionState, type SubscriptionStatus } from '../api';

export interface UseDoctorSubscriptionResult {
  loading: boolean;
  state: DoctorSubscriptionState | null;
  status: SubscriptionStatus | null;
  refresh: () => void;
}

const CHECKOUT_RETURN_POLL_INTERVAL_MS = 2000;
const CHECKOUT_RETURN_POLL_MAX_ATTEMPTS = 20; // ~40s total

export function useDoctorSubscription(enabled: boolean): UseDoctorSubscriptionResult {
  const [loading, setLoading] = useState<boolean>(enabled);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);

  const fetchState = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      setStatus(null);
      return;
    }
    setLoading(true);
    try {
      const s = await apiClient.getSubscriptionStatus();
      setStatus(s);
    } catch (err) {
      // Leave `status` alone (null on first-fetch failure). Downstream
      // consumers treat a null state as "allowed" to avoid locking users
      // out on transient network errors.
      // biome-ignore lint/suspicious/noConsole: dev-only diagnostics
      console.warn('useDoctorSubscription: getSubscriptionStatus failed', err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchState();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, fetchState]);

  // Post-checkout return handler. Stripe redirects back to
  // /settings?subscription=success&session_id=cs_...
  //   1. Immediately call /subscriptions/sync to pull subscription
  //      state DIRECTLY from Stripe (bypasses webhook delivery lag,
  //      which can be seconds to minutes).
  //   2. Then poll /subscriptions/status until state.allowed flips
  //      to true (in case sync failed and we need to wait for the
  //      webhook after all).
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);

    // Read from URL first, then fall back to sessionStorage. main.tsx
    // stashes these at boot BEFORE the client-side router can navigate
    // away from /settings → /dashboard, which strips the URL params.
    let subscriptionFlag = url.searchParams.get('subscription');
    let sessionId = url.searchParams.get('session_id');
    let cameFromStorage = false;
    if (subscriptionFlag !== 'success') {
      try {
        if (sessionStorage.getItem('subscription_return') === 'success') {
          subscriptionFlag = 'success';
          sessionId = sessionStorage.getItem('subscription_session_id');
          cameFromStorage = true;
        }
      } catch {
        // sessionStorage unavailable — fall through
      }
    }
    if (subscriptionFlag !== 'success') return;

    // Visible diagnostic — helps triage when this fix isn't visibly
    // firing (e.g. cached bundle scenarios). Remove after stabilization.
    // biome-ignore lint/suspicious/noConsole: intentional diagnostic
    console.info('[subscription] checkout-return handler firing', {
      hasSessionId: !!sessionId,
      pathname: url.pathname,
      source: cameFromStorage ? 'sessionStorage' : 'url',
    });

    let attempts = 0;
    let cancelled = false;

    const cleanupUrlParams = () => {
      url.searchParams.delete('subscription');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
      try {
        sessionStorage.removeItem('subscription_return');
        sessionStorage.removeItem('subscription_session_id');
      } catch {
        // ignore
      }
    };

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const s = await apiClient.getSubscriptionStatus();
        if (cancelled) return;
        setStatus(s);
        if (s.doctor?.allowed) {
          // biome-ignore lint/suspicious/noConsole: intentional diagnostic
          console.info('[subscription] state now allowed, dismissing modal', {
            reason: s.doctor.reason,
          });
          cleanupUrlParams();
          return; // Success — stop polling
        }
      } catch {
        // Fetch failed — will retry until max attempts.
      }
      if (attempts >= CHECKOUT_RETURN_POLL_MAX_ATTEMPTS) {
        // biome-ignore lint/suspicious/noConsole: intentional diagnostic
        console.warn('[subscription] gave up polling after max attempts');
        cleanupUrlParams();
        return;
      }
      setTimeout(tick, CHECKOUT_RETURN_POLL_INTERVAL_MS);
    };

    // First: try to sync from Stripe directly. This is the primary
    // path — no webhook wait needed. Trigger an IMMEDIATE status
    // refetch after successful sync (instead of waiting for the
    // first poll tick 2s later) so the modal dismisses right away.
    (async () => {
      if (sessionId) {
        try {
          const syncResult = await apiClient.syncSubscriptionFromCheckout(sessionId);
          // biome-ignore lint/suspicious/noConsole: intentional diagnostic
          console.info('[subscription] sync from Stripe result', syncResult);
          if (syncResult?.synced) {
            // Immediate refetch — don't wait 2s for the poll tick.
            try {
              const s = await apiClient.getSubscriptionStatus();
              if (cancelled) return;
              setStatus(s);
              if (s.doctor?.allowed) {
                cleanupUrlParams();
                return; // Done — modal will dismiss on re-render
              }
            } catch {
              // Fall through to polling loop
            }
          }
        } catch (err) {
          // biome-ignore lint/suspicious/noConsole: intentional diagnostic
          console.warn('[subscription] sync from Stripe failed, will poll instead', err);
          // Ignore — polling will still catch webhook-based updates.
        }
      }
      tick();
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return {
    loading,
    state: status?.doctor ?? null,
    status,
    refresh: fetchState,
  };
}
